import React, { useState } from 'react';
import { ChevronRight, Check, LockKeyhole } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { useToast } from '../ui/Toast';
import { ZodiacTheme } from './zodiacThemes';
interface CalendarTabProps {
  theme: ZodiacTheme;
}
export function CalendarTab({
  theme
}: CalendarTabProps) {
  const [hasProCalendar, setHasProCalendar] = useState(false);
  const {
    success
  } = useToast();
  return <div className="space-y-6" data-id="element-15537">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6" data-id="element-15538">
        <div data-id="element-15539">
          <h2 className="text-2xl font-bold text-slate-900" data-id="element-15540">
            Персональный календарь
          </h2>
          <p className="text-sm text-slate-500 mt-1" data-id="element-15541">
            Транзиты и рекомендации на основе вашей натальной карты
          </p>
        </div>
        <div className="flex items-center gap-2 bg-white px-3 py-1.5 rounded-lg border border-slate-200 shadow-sm" data-id="element-15542">
          <span className="text-sm font-medium text-slate-700" data-id="element-15543">
            Октябрь 2024
          </span>
          <div className="flex gap-1 ml-2" data-id="element-15544">
            <button className="p-1 hover:bg-slate-100 rounded" data-id="element-15545">
              <ChevronRight className="w-4 h-4 rotate-180" data-id="element-15546" />
            </button>
            <button className="p-1 hover:bg-slate-100 rounded" data-id="element-15547">
              <ChevronRight className="w-4 h-4" data-id="element-15548" />
            </button>
          </div>
        </div>
      </div>

      {!hasProCalendar ? <Card padding="lg" className="border-indigo-200 bg-gradient-to-br from-indigo-50 to-white relative overflow-hidden" data-id="element-15549">
          <div className="absolute right-0 top-0 w-64 h-64 bg-indigo-500/10 rounded-full blur-3xl" data-id="element-15550"></div>
          <div className="relative z-10 flex flex-col md:flex-row gap-8 items-center" data-id="element-15551">
            <div className="flex-1" data-id="element-15552">
              <Badge variant="pro" className="mb-4" data-id="element-15553">
                PRO Календарь
              </Badge>
              <h3 className="text-2xl font-bold text-slate-900 mb-3" data-id="element-15554">
                Откройте персональные транзиты на каждый день
              </h3>
              <p className="text-slate-600 mb-6" data-id="element-15555">
                Базовый календарь показывает только общие лунные фазы.
                Подключите AstroGuide Plus, чтобы видеть, как планеты влияют
                лично на вас, с ежедневными рекомендациями.
              </p>
              <ul className="space-y-3 mb-6" data-id="element-15556">
                <li className="flex items-center gap-2 text-sm font-medium text-slate-700" data-id="element-15557">
                  <Check className="w-4 h-4 text-emerald-500" data-id="element-15558" /> Индивидуальные
                  благоприятные дни
                </li>
                <li className="flex items-center gap-2 text-sm font-medium text-slate-700" data-id="element-15559">
                  <Check className="w-4 h-4 text-emerald-500" data-id="element-15560" /> Предупреждения
                  о напряженных аспектах
                </li>
                <li className="flex items-center gap-2 text-sm font-medium text-slate-700" data-id="element-15561">
                  <Check className="w-4 h-4 text-emerald-500" data-id="element-15562" /> Синхронизация с
                  Google Calendar
                </li>
              </ul>
              <Button variant="primary" onClick={() => {
            setHasProCalendar(true);
            success('PRO Календарь активирован!');
          }} data-id="element-15563">
                Подключить за 990 ₽/мес
              </Button>
            </div>
            <div className="w-full md:w-1/3 relative" data-id="element-15564">
              <div className="absolute inset-0 bg-gradient-to-t from-white via-transparent to-transparent z-10" data-id="element-15565"></div>
              <div className="bg-white rounded-xl shadow-lg border border-slate-100 p-4 opacity-50 blur-[2px]" data-id="element-15566">
                <div className="h-8 bg-slate-100 rounded mb-3" data-id="element-15567"></div>
                <div className="space-y-2" data-id="element-15568">
                  <div className="h-12 bg-indigo-50 rounded" data-id="element-15569"></div>
                  <div className="h-12 bg-rose-50 rounded" data-id="element-15570"></div>
                  <div className="h-12 bg-emerald-50 rounded" data-id="element-15571"></div>
                </div>
              </div>
              <div className="absolute inset-0 z-20 flex items-center justify-center" data-id="element-15572">
                <div className="bg-white rounded-full p-4 shadow-xl" data-id="element-15573">
                  <LockKeyhole className="w-8 h-8 text-indigo-500" data-id="element-15574" />
                </div>
              </div>
            </div>
          </div>
        </Card> : <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-15575">
          <div className="lg:col-span-2" data-id="element-15576">
            <Card padding="none" className="overflow-hidden" data-id="element-15577">
              <div className="grid grid-cols-7 border-b border-slate-100 bg-slate-50" data-id="element-15578">
                {['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'].map(day => <div key={day} className="py-3 text-center text-xs font-bold text-slate-500 uppercase" data-id="element-15579">
                    {day}
                  </div>)}
              </div>
              <div className="grid grid-cols-7 grid-rows-5 bg-slate-200 gap-px" data-id="element-15580">
                {Array.from({
              length: 35
            }).map((_, i) => {
              const day = i - 1; // Offset for starting day
              const isCurrentMonth = day > 0 && day <= 31;
              const isToday = day === 15;
              const hasGoodAspect = day === 12 || day === 18 || day === 25;
              const hasBadAspect = day === 14 || day === 22;
              return <div key={i} className={`min-h-[100px] p-2 ${isCurrentMonth ? 'bg-white' : 'bg-slate-50 text-slate-400'} ${isToday ? `ring-2 inset-0 z-10 ${theme.ring}` : ''} relative group cursor-pointer hover:bg-slate-50 transition-colors`} data-id="element-15581">
                      <span className={`text-sm font-medium ${isToday ? theme.text : ''}`} data-id="element-15582">
                        {isCurrentMonth ? day : ''}
                      </span>

                      {isCurrentMonth && <div className="mt-2 space-y-1" data-id="element-15583">
                          {hasGoodAspect && <div className="text-[10px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded truncate" data-id="element-15584">
                              Трин Венера
                            </div>}
                          {hasBadAspect && <div className="text-[10px] bg-rose-100 text-rose-700 px-1.5 py-0.5 rounded truncate" data-id="element-15585">
                              Квадрат Марс
                            </div>}
                          {day === 10 && <div className="text-[10px] bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded truncate" data-id="element-15586">
                              🌑 Новолуние
                            </div>}
                        </div>}
                    </div>;
            })}
              </div>
            </Card>
          </div>
          <div className="lg:col-span-1 space-y-4" data-id="element-15587">
            <Card padding="md" className={`border-t-4 ${theme.border.replace('border-', 'border-t-')}`} data-id="element-15588">
              <h3 className="font-bold text-slate-900 mb-4" data-id="element-15589">
                Прогноз на сегодня (15 Окт)
              </h3>
              <div className="space-y-4" data-id="element-15590">
                <div className="flex gap-3" data-id="element-15591">
                  <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0 text-emerald-600" data-id="element-15592">
                    ↑
                  </div>
                  <div data-id="element-15593">
                    <div className="text-sm font-bold text-slate-900" data-id="element-15594">
                      Финансы и карьера
                    </div>
                    <div className="text-xs text-slate-600 mt-1" data-id="element-15595">
                      Отличный день для переговоров. Меркурий в секстиле к
                      вашему натальному Юпитеру.
                    </div>
                  </div>
                </div>
                <div className="flex gap-3" data-id="element-15596">
                  <div className="w-8 h-8 rounded-full bg-rose-100 flex items-center justify-center flex-shrink-0 text-rose-600" data-id="element-15597">
                    ↓
                  </div>
                  <div data-id="element-15598">
                    <div className="text-sm font-bold text-slate-900" data-id="element-15599">
                      Отношения
                    </div>
                    <div className="text-xs text-slate-600 mt-1" data-id="element-15600">
                      Возможны недопонимания вечером. Постарайтесь не принимать
                      важных решений.
                    </div>
                  </div>
                </div>
              </div>
              <Button variant="outline" fullWidth className="mt-6" data-id="element-15601">
                Подробный прогноз
              </Button>
            </Card>
          </div>
        </div>}
    </div>;
}